/**
 * Phase 2 custom domains at boot (PM 01fda321, decision 14).
 *
 * On a club's own domain (gymnasticsofyork.com) the URL carries no slug and
 * the host names no club by itself, so before React mounts the boot asks the
 * backend which club this host is (GET /public/bootstrap-by-host). The
 * backend answers only for an ACTIVE domain; anything else is not_found and
 * the app shows the not-found page, never a default club.
 *
 * A found club is recorded with setResolvedCustomDomainSlug and the path gets
 * the slug injected with the phase 1 rule (rewritePathForHostSlug: only a
 * first segment equal to the slug is the slug), so every /:clubid route and
 * link works unchanged, exactly as on <slug>.clubcentral.app.
 *
 * Platform hosts, staging hosts, localhost and <slug>.clubcentral.app never
 * reach the network here: isCustomDomainHost is false and the boot is what it
 * was.
 */
import { fetchTenantBootstrapByHost, type BootstrapResult } from "./bootstrapAPI";
import { isCustomDomainHost, setResolvedCustomDomainSlug } from "./customDomainHost";
import { rewritePathForHostSlug } from "./apiHelpers";

export type CustomDomainBoot =
  | { status: "platform" }
  | { status: "found"; slug: string; rewritePath: string | null }
  | { status: "not_found"; host: string }
  | { status: "error"; host: string };

interface BootLocation {
  hostname: string;
  pathname: string;
  search: string;
  hash: string;
}

export async function resolveCustomDomainBoot(
  location: BootLocation,
  fetchByHost: (host: string) => Promise<BootstrapResult> = fetchTenantBootstrapByHost,
): Promise<CustomDomainBoot> {
  const host = (location.hostname || "").toLowerCase();
  if (!isCustomDomainHost(host)) return { status: "platform" };

  const result = await fetchByHost(host);
  if (result.status === "not_found") {
    setResolvedCustomDomainSlug(host, null);
    return { status: "not_found", host };
  }
  if (result.status !== "ok") {
    setResolvedCustomDomainSlug(host, null);
    return { status: "error", host };
  }

  const slug = result.data.tenant.slug;
  setResolvedCustomDomainSlug(host, slug);
  return { status: "found", slug, rewritePath: rewritePathForHostSlug(slug, location) };
}
